import { io, Socket } from 'socket.io-client';

const CHAT_URL = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:5001';

let socket: Socket | null = null;
let currentToken: string | null = null;

// ─────────────────────────────────────────────────────────────
// Global refresh registry
// Pages register a callback when they mount; socket fires it
// when the server tells this client to re-fetch data.
// Key = a stable string identifier (e.g. 'dm-123', 'group-456')
// ─────────────────────────────────────────────────────────────
type RefreshCallback = () => void;
const refreshRegistry: Map<string, RefreshCallback> = new Map();

export const registerRefreshCallback = (key: string, cb: RefreshCallback) => {
  refreshRegistry.set(key, cb);
};

export const unregisterRefreshCallback = (key: string) => {
  refreshRegistry.delete(key);
};

// ─────────────────────────────────────────────────────────────

const parseUserIdFromToken = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.userId ?? null;
  } catch {
    return null;
  }
};

const emitJoinUserRoom = (token: string) => {
  if (!socket?.connected) return;
  const userId = parseUserIdFromToken(token);
  if (userId) {
    socket.emit('join-user-room', userId);
    console.log('🔗 join-user-room emitted for user', userId);
  }
};

export const connectSocket = (token: string): Socket => {
  if (socket && currentToken === token && socket.connected) {
    return socket;
  }

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    currentToken = null;
  }

  currentToken = token;

  socket = io(CHAT_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  const onSocketConnect = () => {
    console.log('✅ Socket connected:', socket?.id);
    emitJoinUserRoom(token);
  };

  socket.on('connect', onSocketConnect);

  socket.on('reconnect', (attempt) => {
    console.log('🔄 Socket reconnected (attempt', attempt, ')');
    emitJoinUserRoom(token);
  });

  socket.on('disconnect', (reason) => {
    console.log('❌ Socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
  });

  socket.on('force-disconnect', ({ reason }: { reason: string }) => {
    const nextPath =
      reason === 'signed_in_elsewhere'
        ? '/login?reason=signed_in_elsewhere'
        : '/login';
    localStorage.removeItem('token');
    disconnectSocket();
    if (`${window.location.pathname}${window.location.search}` !== nextPath) {
      window.location.replace(nextPath);
    }
  });

  // ─────────────────────────────────────────────────────────
  // Global refresh listener
  //
  // Payload shapes:
  //   { type: 'dm',          otherUserId: number }
  //   { type: 'dm-sidebar'                       }
  //   { type: 'group',       storyId: number     }
  //   { type: 'custom-group', groupId: number    }
  //   { type: 'custom-group-list'                }
  //
  // FIX: removed early `return` after firing the dm-specific
  // callback — the sidebar callback now ALWAYS fires for any
  // DM event so unread badges stay accurate on device 2
  // regardless of which conversation is currently open.
  // ─────────────────────────────────────────────────────────
  socket.on('refresh-needed', (payload: {
    type: 'dm' | 'dm-sidebar' | 'group' | 'custom-group' | 'custom-group-list';
    otherUserId?: number;
    storyId?: number;
    groupId?: number;
  }) => {
    console.log('🔄 refresh-needed received', payload);

    if (payload.type === 'dm-sidebar') {
      // Explicit sidebar-only ping (sent by server when receiver's
      // specific DM page isn't open yet)
      const sidebarCb = refreshRegistry.get('dm-sidebar');
      if (sidebarCb) sidebarCb();
      return;
    }

    if (payload.type === 'dm' && payload.otherUserId != null) {
      // Fire the conversation-specific callback if that chat is open
      const dmCb = refreshRegistry.get(`dm-${payload.otherUserId}`);
      if (dmCb) dmCb();

      // FIX: always also fire sidebar — do NOT early-return after dmCb.
      // This keeps the unread badge in the sidebar accurate even when
      // the specific DM page is open (it was previously skipped).
      const sidebarCb = refreshRegistry.get('dm-sidebar');
      if (sidebarCb) sidebarCb();
      return;
    }

    if (payload.type === 'group' && payload.storyId != null) {
      const cb = refreshRegistry.get(`group-${payload.storyId}`);
      if (cb) cb();
      return;
    }

    if (payload.type === 'custom-group-list') {
      refreshRegistry.get('custom-group-list')?.();
      return;
    }

    if (payload.type === 'custom-group' && payload.groupId != null) {
      refreshRegistry.get(`custom-group-${payload.groupId}`)?.();
      refreshRegistry.get('custom-group-list')?.();
    }
  });

  return socket;
};

export const ensureUserRoom = (token: string) => {
  emitJoinUserRoom(token);
};

export const getSocket = (): Socket | null => socket;

export const disconnectSocket = (): void => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    currentToken = null;
  }
};
